import * as React from 'react';
import { useState } from 'react';

import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
} from '@mui/material';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BlockIcon from '@mui/icons-material/Block';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';

import axios from '../axios';
import AuthService from '../helpers/auth/AuthService';
import { User } from '../types/User';

type UserListProps = {
  users: User[];
  onUserUpdated?: () => void;
};

export default function UserList({ users, onUserUpdated }: UserListProps) {
  const [snackMessage, setSnackMessage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const auth = AuthService.getCurrentUser();
  const isAdmin = auth?.admin;

  // Map API response to our frontend model
  const mapUser = (user: User) => {
    return user; // Return the user as-is
  };

  const handleErrClose = () => {
    setSnackMessage('');
  };

  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
    });
  };

  const handleConfirmClose = () => {
    setConfirmDialog({
      ...confirmDialog,
      open: false,
    });
  };

  const handleConfirm = () => {
    confirmDialog.onConfirm();
    handleConfirmClose();
  };

  const handleSetAdmin = (userId: string, makeAdmin: boolean) => {
    if (userId === auth.user_id) {
      setSnackMessage('Cannot modify your own admin status');
      return;
    }

    const config = {
      headers: { Authorization: `Bearer ${auth.access_token}` },
    };

    axios
      .post(
        '/user/set-admin',
        {
          user_id: userId,
          admin: makeAdmin,
        },
        config
      )
      .then(() => {
        setSnackMessage(
          `User ${makeAdmin ? 'promoted to' : 'removed from'} admin role`
        );
        if (onUserUpdated) onUserUpdated();
      })
      .catch((err) => {
        setSnackMessage(err.response?.data || 'Error updating admin status');
      });
  };

  const handleDisableUser = (userId: string) => {
    if (userId === auth.user_id) {
      setSnackMessage('Cannot disable your own account');
      return;
    }

    const config = {
      headers: { Authorization: `Bearer ${auth.access_token}` },
    };

    axios
      .post(
        '/user/disable',
        {
          user_id: userId,
        },
        config
      )
      .then(() => {
        setSnackMessage('User disabled successfully');
        if (onUserUpdated) onUserUpdated();
      })
      .catch((err) => {
        setSnackMessage(err.response?.data || 'Error disabling user');
      });
  };

  const handleEnableUser = (userId: string) => {
    const config = {
      headers: { Authorization: `Bearer ${auth.access_token}` },
    };

    axios
      .post(
        '/user/enable',
        {
          user_id: userId,
        },
        config
      )
      .then(() => {
        setSnackMessage('User enabled successfully');
        if (onUserUpdated) onUserUpdated();
      })
      .catch((err) => {
        setSnackMessage(err.response?.data || 'Error enabling user');
      });
  };

  const handlePasswordReset = (userId: string) => {
    const config = {
      headers: { Authorization: `Bearer ${auth.access_token}` },
    };

    axios
      .post(
        '/user/send-reset',
        {
          user_id: userId,
        },
        config
      )
      .then((res) => {
        // Make sure we're using the reset_link exactly as received from the API
        const resetLink = res.data.reset_link;
        // Ensure we're using the full URL
        const fullResetLink = window.location.origin + resetLink;
        navigator.clipboard.writeText(fullResetLink);
        setSnackMessage('Password reset link copied to clipboard');
      })
      .catch((err) => {
        setSnackMessage(err.response?.data || 'Error generating reset link');
      });
  };

  if (users) {
    return (
      <React.Fragment>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User ID</TableCell>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              {isAdmin && <TableCell align='right'>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((apiUser: User) => {
              const user = mapUser(apiUser);
              return (
                <TableRow hover key={user.ID}>
                  <TableCell>{user.ID}</TableCell>
                  <TableCell>{user.Firstname}</TableCell>
                  <TableCell>{user.Lastname}</TableCell>
                  <TableCell>{user.Email}</TableCell>
                  <TableCell>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      {user.AdminRole ? (
                        <Tooltip title='Admin'>
                          <AdminPanelSettingsIcon color='primary' />
                        </Tooltip>
                      ) : (
                        <Tooltip title='User'>
                          <PersonIcon />
                        </Tooltip>
                      )}
                      {user.DeletedAt !== null && (
                        <Tooltip title='Account Disabled'>
                          <BlockIcon color='error' />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  {isAdmin && (
                    <TableCell align='right'>
                      {user.ID !== auth.ID && (
                        <>
                          <Tooltip
                            title={
                              user.AdminRole ? 'Remove Admin' : 'Make Admin'
                            }
                          >
                            <IconButton
                              onClick={() =>
                                showConfirmDialog(
                                  user.AdminRole
                                    ? 'Remove Admin Role?'
                                    : 'Grant Admin Role?',
                                  `Are you sure you want to ${user.AdminRole ? 'remove' : 'grant'} admin role for ${user.Email}?`,
                                  () => handleSetAdmin(user.ID, !user.AdminRole)
                                )
                              }
                              color={user.AdminRole ? 'primary' : 'default'}
                            >
                              <AdminPanelSettingsIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              user.DeletedAt === null
                                ? 'Disable User'
                                : 'Enable User'
                            }
                          >
                            <IconButton
                              onClick={() =>
                                showConfirmDialog(
                                  user.DeletedAt === null
                                    ? 'Disable User?'
                                    : 'Enable User?',
                                  `Are you sure you want to ${user.DeletedAt === null ? 'disable' : 'enable'} ${user.Email}?`,
                                  () => {
                                    if (user.DeletedAt === null) {
                                      handleDisableUser(user.ID);
                                    } else {
                                      handleEnableUser(user.ID);
                                    }
                                  }
                                )
                              }
                              color={
                                user.DeletedAt !== null ? 'error' : 'default'
                              }
                            >
                              {user.DeletedAt === null ? (
                                <PersonOffIcon />
                              ) : (
                                <PersonIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title='Reset Password'>
                        <IconButton
                          onClick={() => handlePasswordReset(user.ID)}
                        >
                          <LockResetIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Snackbar
          open={snackMessage !== ''}
          autoHideDuration={6000}
          message={snackMessage}
          onClose={handleErrClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: '#E50C45',
              minWidth: 'auto',
              padding: '6px 16px',
            },
          }}
        />
        <Dialog open={confirmDialog.open} onClose={handleConfirmClose}>
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Typography>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmClose}>Cancel</Button>
            <Button onClick={handleConfirm} color='primary'>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  } else {
    return <Typography>No users</Typography>;
  }
}
